import type { VerificationAnswer } from "@ondo/shared";
import type { Ref } from "react";

import { VerificationActions } from "./VerificationActions.tsx";
import type { Capacity, RequestCard } from "./verifications.model.ts";
import { RESOLUTION_COPY, VERIFICATION_COPY } from "./verifications.constants.ts";

type Props = {
  /** So a link can scroll to this card. See `cardId` in `Verifications.tsx`. */
  id: string;
  /** Whether a link asked for this one, which draws the rule beside it. */
  focused: boolean;
  /** Attached by the screen to the card a link asked for, so it can be scrolled to. */
  ref?: Ref<HTMLLIElement>;
  card: RequestCard;
  capacities: Capacity[];
  imageUrl: string | null;
  selected: boolean;
  busy: boolean;
  onToggle: () => void;
  onAnswer: (body: {
    answer: VerificationAnswer;
    body?: string | null;
    onBehalfOf?: string | null;
  }) => void;
  onRefer: (ondoId: string, note: string | null) => void;
  onAddDocument: (files: File[]) => void;
  onOpenDocument: (documentId: string) => void;
};

export function VerificationCard({
  id,
  focused,
  ref,
  card,
  capacities,
  imageUrl,
  selected,
  busy,
  onToggle,
  onAnswer,
  onRefer,
  onAddDocument,
  onOpenDocument,
}: Props) {
  return (
    <li
      ref={ref}
      id={id}
      className={`verifications__card verifications__card--${card.aspect} ${
        card.awaiting ? "verifications__card--awaiting" : ""
      } ${focused ? "verifications__card--focused" : ""}`}
    >
      <header className="verifications__card-head">
        {card.canAnswer && (
          <label className="verifications__select">
            <input type="checkbox" checked={selected} onChange={onToggle} />
            <span className="verifications__sr">Select for a bulk answer</span>
          </label>
        )}
        <div className="verifications__card-title">
          <h3>{card.title}</h3>
          <p>{card.blurb}</p>
        </div>
        <time dateTime={card.askedOn}>{card.askedOnLabel}</time>
        {/* The act, not the fact of one. See `Resolution`. */}
        <span
          className={`verifications__stamp verifications__stamp--${
            card.resolution ? card.resolution.tone : "awaiting"
          }`}
        >
          {card.resolution ? card.resolution.label : VERIFICATION_COPY.awaitingBadge}
        </span>
      </header>

      <div className="verifications__parties">
        <section className="verifications__party">
          <h4>{VERIFICATION_COPY.requestedBy}</h4>
          {/* Null renders as a word, not a gap. The server types it nullable. */}
          {card.requestedBy ? (
            <>
              <p className="verifications__party-name">{card.requestedBy.name}</p>
              <p className="verifications__party-id">{card.requestedBy.ondoId}</p>
            </>
          ) : (
            <p className="verifications__party-name">{VERIFICATION_COPY.withheld}</p>
          )}
          {card.askedOf && (
            <p className="verifications__party-id">
              {VERIFICATION_COPY.askedOf} {card.askedOf.name} · {card.askedOf.ondoId}
            </p>
          )}
          {/*
            The asker's own words about what they had in mind. Rendered as their
            claim rather than as a role chip, because the registry records no
            role here and a chip would read as one it had verified.
          */}
          {card.capacityHint && (
            <p className="verifications__hint">
              {VERIFICATION_COPY.capacityHint} {card.capacityHint}
            </p>
          )}
        </section>

        <section className="verifications__party verifications__work">
          <h4>{VERIFICATION_COPY.workInQuestion}</h4>
          <div className="verifications__work-row">
            {imageUrl ? (
              <img src={imageUrl} alt="" className="verifications__thumb" />
            ) : (
              <span className="verifications__thumb verifications__thumb--empty" aria-hidden />
            )}
            <div>
              <p className="verifications__party-name">{card.work.title}</p>
              <p className="verifications__party-id">{card.work.byline}</p>
            </div>
          </div>
        </section>
      </div>

      <section className="verifications__asked">
        <h4>{VERIFICATION_COPY.whatToVerify}</h4>
        <p>{card.what}</p>
      </section>

      {card.dates.length > 0 && (
        <section className="verifications__dates">
          <h4>{VERIFICATION_COPY.datesAndContext}</h4>
          <ul>
            {card.dates.map((chip) => (
              <li key={`${chip.label}-${chip.value}`}>
                <strong>{chip.value}</strong> {chip.label}
              </li>
            ))}
          </ul>
        </section>
      )}

      {card.documents.length > 0 && (
        <section className="verifications__documents">
          <h4>
            {VERIFICATION_COPY.documentsToReview} ({card.documents.length})
          </h4>
          <ul>
            {card.documents.map((document) => (
              <li key={document.id}>
                <div>
                  {/* A button, not an anchor: nothing is signed until somebody
                      asks to look, so there is no href to put here and an
                      anchor without one is a lie about what the control does. */}
                  <button
                    type="button"
                    className="verifications__document-name"
                    aria-label={document.openLabel}
                    onClick={() => onOpenDocument(document.id)}
                  >
                    {document.name}
                  </button>
                  <p className="verifications__party-id">{document.meta}</p>
                </div>
                {document.status && (
                  <span
                    className={`verifications__stamp verifications__stamp--${document.status.tone}`}
                  >
                    {document.status.label}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {card.resolution && (
        <section
          className={`verifications__recorded verifications__recorded--${card.resolution.tone}`}
        >
          <h4>{RESOLUTION_COPY.title}</h4>
          <p className="verifications__recorded-lead">{card.resolution.lead}</p>

          {card.resolution.reason && (
            <>
              <h5>{card.resolution.reason.label}</h5>
              {/* The answerer's own words, quoted rather than restated. */}
              <blockquote className="verifications__recorded-body">
                {card.resolution.reason.body}
              </blockquote>
            </>
          )}

          {card.resolution.silent && (
            <p className="verifications__help">{card.resolution.silent}</p>
          )}
        </section>
      )}

      <VerificationActions
        card={card}
        capacities={capacities}
        busy={busy}
        onAnswer={onAnswer}
        onRefer={onRefer}
        onAddDocument={onAddDocument}
      />
    </li>
  );
}
